import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Trash2, Upload } from 'lucide-react';
import {
  Badge,
  Button,
  Callout,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Input,
  KeyValueList,
  NativeSelect,
  PageHeader,
  Section,
  Skeleton,
  StatusDot,
  Table,
  TableContainer,
  TableMessage,
  TableSkeleton,
  TBody,
  Td,
  Th,
  THead,
  Tr,
  useToast,
} from '@zentera/ui';
import { api, ApiError, ChatThread, fmt, PROFILE_LABEL, TraceEvent, useTestChat, type EngineEvent } from '@zentera/web-core';
import { usePageTitle } from '../components/shell';
import { useCan } from '../lib/auth';

interface Status {
  config: { receivedAt: string; profiles: { key: string; name: string }[]; integration: { certified: boolean; enabledOperations: number } } | null;
  aiService: { active: boolean };
  knowledge: { version: number; chunkCount: number; semanticSearch: boolean; publishedAt: string } | null;
  pendingChanges: boolean;
}

/* ───────────── Assistente: estado e publicação ───────────── */

export function AssistantPage() {
  usePageTitle('Assistente');
  const can = useCan();
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState('');
  const status = useQuery({ queryKey: ['assistant-status'], queryFn: () => api.get<Status>('/api/assistant/status') });
  const versions = useQuery({ queryKey: ['knowledge-versions'], queryFn: () => api.get<{ items: { id: string; version: number; status: string; chunkCount: number; semanticSearch: boolean; note: string | null; createdAt: string }[] }>('/api/knowledge/versions') });
  const publish = useMutation({
    mutationFn: () => api.post<{ version: { version: number; chunkCount: number }; semanticSearch: boolean }>('/api/knowledge/publish', { note: note || undefined }),
    onSuccess: (r) => {
      setNote('');
      void qc.invalidateQueries({ queryKey: ['assistant-status'] });
      void qc.invalidateQueries({ queryKey: ['knowledge-versions'] });
      toast({
        tone: 'success',
        title: `Versão ${r.version.version} publicada`,
        description: `${r.version.chunkCount} trechos disponíveis para o assistente.`,
      });
    },
    onError: (e) => toast({ tone: 'danger', title: 'Falha ao publicar', description: e instanceof ApiError ? e.message : undefined }),
  });

  if (status.isError) return <ErrorState error={status.error} onRetry={() => void status.refetch()} />;
  if (!status.data) return <Skeleton className="h-96 rounded-lg" />;
  const s = status.data;

  return (
    <div className="space-y-10">
      <PageHeader title="Assistente virtual" description="O comportamento do assistente é definido pela Zentera. Aqui você publica o conhecimento que ele usa e acompanha o que está ativo nesta instalação." />

      <Section
        title="Conhecimento publicado"
        description="O assistente só usa o conteúdo publicado. Edições em planos, políticas, FAQ, horários e documentos ficam pendentes até a próxima publicação."
      >
        {s.pendingChanges && (
          <Callout tone="warning" className="mb-4" title="Há alterações não publicadas">
            O assistente ainda responde com a versão anterior.
          </Callout>
        )}
        <KeyValueList
          items={[
            { label: 'Versão em uso', value: s.knowledge ? `v${s.knowledge.version} · ${fmt.dateTime(s.knowledge.publishedAt)}` : <span className="text-fg-4">Nenhuma publicada</span> },
            { label: 'Trechos', value: s.knowledge ? <span className="tabular">{fmt.int(s.knowledge.chunkCount)}</span> : '—' },
            {
              label: 'Busca',
              value: s.knowledge?.semanticSearch ? 'Por significado e palavras-chave' : 'Por palavras-chave',
            },
          ]}
        />
        {can('knowledge.write') && (
          <div className="mt-4 flex max-w-xl items-end gap-2">
            <Field label="Nota da publicação" optional className="flex-1">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex.: novos planos de novembro" />
            </Field>
            <Button variant="primary" loading={publish.isPending} onClick={() => publish.mutate()}>
              Publicar conhecimento
            </Button>
          </div>
        )}
      </Section>

      <Section title="Histórico de publicações">
        <TableContainer>
          <Table>
            <THead><tr><Th>Versão</Th><Th>Situação</Th><Th align="right">Trechos</Th><Th>Busca</Th><Th>Nota</Th><Th>Publicada</Th></tr></THead>
            <TBody>
              {versions.isPending ? <TableSkeleton columns={6} rows={2} /> : (versions.data?.items ?? []).length === 0 ? (
                <TableMessage columns={6}><EmptyState compact title="Nenhuma publicação ainda" /></TableMessage>
              ) : (
                versions.data!.items.map((v) => (
                  <Tr key={v.id}>
                    <Td className="font-mono text-xs">v{v.version}</Td>
                    <Td>{v.status === 'published' ? <Badge tone="success">Em uso</Badge> : <Badge tone="muted">Arquivada</Badge>}</Td>
                    <Td align="right">{v.chunkCount}</Td>
                    <Td className="text-xs text-fg-3">{v.semanticSearch ? 'significado + palavras' : 'palavras-chave'}</Td>
                    <Td className="max-w-xs truncate text-fg-2">{v.note ?? '—'}</Td>
                    <Td>{fmt.dateTime(v.createdAt)}</Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </TableContainer>
      </Section>

      <div className="grid gap-x-12 gap-y-8 lg:grid-cols-2">
        <Section title="Configuração recebida da Zentera">
          {s.config ? (
            <KeyValueList
              items={[
                { label: 'Recebida', value: fmt.dateTime(s.config.receivedAt) },
                { label: 'Perfis ativos', value: s.config.profiles.map((p) => p.name).join(', ') || '—' },
                { label: 'Integração com o sistema de gestão', value: s.config.integration.certified ? `${s.config.integration.enabledOperations} consultas liberadas` : 'Não ativada — o assistente não consulta dados de clientes' },
              ]}
            />
          ) : (
            <p className="text-sm text-fg-3">Aguardando a primeira configuração da Zentera.</p>
          )}
        </Section>
        <Section title="Serviço de IA">
          <KeyValueList
            items={[
              {
                label: 'Situação',
                value: s.aiService.active ? <StatusDot tone="success">Ativo</StatusDot> : <StatusDot tone="muted">Aguardando ativação pela Zentera</StatusDot>,
              },
            ]}
          />
          <p className="mt-3 text-sm text-fg-3">O serviço de IA é operado e monitorado pela Zentera, com redundância automática.</p>
        </Section>
      </div>
    </div>
  );
}

/* ───────────── Documentos ───────────── */

interface Doc { id: string; name: string; mime: string; sizeBytes: number; pages: number | null; chars: number; createdAt: string }

export function DocumentsPage() {
  usePageTitle('Documentos');
  const can = useCan();
  const qc = useQueryClient();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const q = useQuery({ queryKey: ['documents'], queryFn: () => api.get<{ items: Doc[] }>('/api/knowledge/documents') });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > 5 * 1024 * 1024) throw new Error('Arquivo maior que 5 MB');
      const mime = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : file.name.toLowerCase().endsWith('.md') ? 'text/markdown' : 'text/plain';
      const buf = new Uint8Array(await file.arrayBuffer());
      let bin = '';
      for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
      return api.post<{ pages: number | null; chars: number }>('/api/knowledge/documents', { name: file.name, mime, contentBase64: btoa(bin) });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: ['assistant-status'] });
      toast({ tone: 'success', title: 'Documento adicionado', description: `${fmt.int(r.chars)} caracteres extraídos. Publique o conhecimento para o assistente usar.` });
    },
    onError: (e) => toast({ tone: 'danger', title: 'Não foi possível adicionar', description: (e as Error).message }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/knowledge/documents/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: ['assistant-status'] });
    },
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Documentos"
        description="Contratos-modelo, regulamentos e manuais em PDF ou texto. O conteúdo é extraído e indexado somente nesta instalação."
        actions={
          can('knowledge.write') && (
            <>
              <input ref={input} type="file" accept=".pdf,.txt,.md,application/pdf,text/plain" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />
              <Button variant="primary" icon={<Upload />} loading={upload.isPending} onClick={() => input.current?.click()}>Adicionar documento</Button>
            </>
          )
        }
      />
      <TableContainer>
        <Table>
          <THead><tr><Th>Documento</Th><Th align="right">Tamanho</Th><Th align="right">Páginas</Th><Th align="right">Texto extraído</Th><Th>Adicionado</Th><Th className="w-10"><span className="sr-only">Ações</span></Th></tr></THead>
          <TBody>
            {q.isPending ? <TableSkeleton columns={6} rows={3} /> : q.isError ? (
              <TableMessage columns={6}><ErrorState error={q.error} /></TableMessage>
            ) : q.data.items.length === 0 ? (
              <TableMessage columns={6}><EmptyState icon={<FileText />} title="Nenhum documento" description="PDFs digitalizados sem texto (imagem) não podem ser lidos; envie a versão com texto selecionável. Limite de 5 MB." /></TableMessage>
            ) : (
              q.data.items.map((d) => (
                <Tr key={d.id}>
                  <Td className="font-medium">{d.name}</Td>
                  <Td align="right">{(d.sizeBytes / 1024).toFixed(0)} KB</Td>
                  <Td align="right">{d.pages ?? '—'}</Td>
                  <Td align="right">{fmt.int(d.chars)} caract.</Td>
                  <Td className="text-fg-2">{fmt.relative(d.createdAt)}</Td>
                  <Td>{can('knowledge.write') && <IconButton size="sm" label={`Remover ${d.name}`} icon={<Trash2 />} onClick={() => remove.mutate(d.id)} />}</Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      </TableContainer>
    </div>
  );
}

/* ───────────── Testar assistente ───────────── */

type TestState = { status: string; activeProfile: string; profileName: string | null; identified: boolean };
type TurnExtra = { events: EngineEvent[]; state: TestState; labels: Record<string, string> };
type TestConversation = { id: string; persona: string; mode: string; groupWindowSeconds: number };

export function TestAssistantPage() {
  usePageTitle('Testar assistente');
  const [persona, setPersona] = useState<'maria' | 'joao' | 'desconhecido'>('maria');
  const [mode, setMode] = useState<'ai' | 'simulation'>('simulation');
  const [conv, setConv] = useState<TestConversation | null>(null);
  const start = useMutation({
    mutationFn: () => api.post<TestConversation>('/api/assistant/test-conversations', { persona, mode }),
    onSuccess: setConv,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Testar assistente"
        description="Converse com o assistente desta instalação usando o conhecimento publicado. Os dados de clientes são fictícios (sistema de gestão simulado) — nenhum cliente real é consultado."
      />
      <div className="flex flex-wrap items-end gap-3 border-b border-line pb-5">
        <Field label="Simular como">
          <NativeSelect value={persona} onChange={(e) => setPersona(e.target.value as typeof persona)} className="w-72">
            <option value="maria">Maria · cliente fictícia (CPF 529.982.247-25)</option>
            <option value="joao">João · cliente fictício (CPF 111.444.777-35)</option>
            <option value="desconhecido">Número sem cadastro</option>
          </NativeSelect>
        </Field>
        <Field label="Modelo">
          <NativeSelect value={mode} onChange={(e) => setMode(e.target.value as typeof mode)} className="w-64">
            <option value="simulation">Simulação por regras (sem IA)</option>
            <option value="ai">IA configurada pela Zentera</option>
          </NativeSelect>
        </Field>
        <Button variant="primary" loading={start.isPending} onClick={() => start.mutate()}>{conv ? 'Nova conversa' : 'Iniciar conversa'}</Button>
        {conv && <span className="text-xs text-fg-3">{conv.persona} · {conv.mode === 'simulation' ? 'simulação por regras' : 'IA'}</span>}
      </div>

      {!conv ? (
        <EmptyState title="Escolha o cliente fictício e o modelo" description="O modo de simulação não usa IA: serve para verificar o conhecimento publicado e as regras de segurança. Use o modo IA para avaliar as respostas reais. Mensagens de voz usam a IA para transcrever." />
      ) : (
        <TestChat key={conv.id} conv={conv} />
      )}
    </div>
  );
}

function TestChat({ conv }: { conv: TestConversation }) {
  const [selected, setSelected] = useState<number | null>(null);
  const chat = useTestChat<TurnExtra>({
    windowSeconds: conv.groupWindowSeconds,
    post: async (parts) => {
      try {
        const r = await api.post<{ reply: string | null; events: EngineEvent[]; state: TestState; responseMs: number; transcripts: string[]; profiles: Record<string, string> }>(
          `/api/assistant/test-conversations/${conv.id}/messages`,
          { parts },
        );
        return { reply: r.reply, profileName: r.state.profileName, responseMs: r.responseMs, transcripts: r.transcripts, extra: { events: r.events, state: r.state, labels: r.profiles } };
      } catch (e) {
        throw new Error(e instanceof ApiError ? e.message : 'Falha ao enviar', { cause: e });
      }
    },
  });
  const last = chat.turns.at(-1);
  const current = chat.turns.find((t) => t.id === selected) ?? last;
  const state = last?.extra.state;
  const labels = last?.extra.labels ?? {};

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
      <ChatThread
        chat={chat}
        windowSeconds={conv.groupWindowSeconds}
        selectedId={current?.id ?? null}
        onSelect={setSelected}
        title="Assistente virtual"
        subtitle={
          state ? (
            <>
              {state.status === 'human' ? 'Transferido para atendente' : `Setor: ${state.profileName ?? labels[state.activeProfile] ?? PROFILE_LABEL[state.activeProfile] ?? state.activeProfile}`}
              {' · '}
              {state.identified ? 'cliente identificado' : 'não identificado'}
            </>
          ) : (
            'online'
          )
        }
        placeholder="Ex.: quero a segunda via da minha fatura"
      />

      <aside className="rounded-lg border border-line" aria-label="Rastro do motor">
        <div className="border-b border-line px-4 py-2.5 text-xs font-medium text-fg-2">Rastro da resposta</div>
        {!current ? (
          <p className="px-4 py-6 text-sm text-fg-4">Cada resposta mostra o conhecimento consultado, as chamadas ao modelo e as decisões do Gateway de Segurança.</p>
        ) : (
          <ol className="divide-y divide-line">
            {current.extra.events.map((e, i) => (
              <li key={i} className="px-4 py-2.5 text-xs">
                <TraceEvent e={e} labels={current.extra.labels} />
              </li>
            ))}
          </ol>
        )}
      </aside>
    </div>
  );
}
